import {
  Alert,
  Box,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Toolbar,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Apps, useAppContext } from '../contexts/AppContext';
import React, { useEffect, useRef, useState } from 'react';
import { Server } from '../services/Server';
import { useNavigate, useParams } from 'react-router-dom';
import { handleError } from '../utils/Error';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery } from '@tanstack/react-query';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { generatePages } from './Pages';
import AppSoilingAssessment from '../components/app-assessment/AppSoilingAssessment';
import AppSoilingMitigationStrategy from '../components/app-optimization/AppSoilingMitigationStrategy.tsx';
import { useFormContext } from '../contexts/FormContext';
import FormRenderer from '../components/form-engine/FormRenderer';
import type { PageSchema } from '../components/form-engine/types';
import { getSoilingBlockKeys } from '../utils/soilingSensor';
import { useFormData } from '../hooks/useFormValues';
import { useSensorData } from '../hooks/useSensorData';
import { useProjectValidation } from '../hooks/useProjectValidation';
import { useProjectMutations } from '../hooks/useProjectMutations';
import AppSoilingOperationalStrategy from '../components/app-optimization/AppSoilingOperationalStrategy';

const ROLE_SECTIONS: { role: string; section: string; label: string; icon: React.ReactNode }[] = [
  { role: 'assessment', section: 'soiling_risk_assessment', label: 'Soiling Assessment', icon: <AssessmentIcon /> },
  { role: 'mitigation_strategy', section: 'mitigation_strategy', label: 'Mitigation Strategy', icon: <CompareArrowsIcon /> },
  { role: 'ex_post_analysis', section: 'ex_post_analysis', label: 'Ex-Post Analysis', icon: <CompareArrowsIcon /> },
  { role: 'operational_strategy', section: 'operational_strategy', label: 'Operational Strategy', icon: <CompareArrowsIcon /> },
];

const Project = () => {
  const { role, completed, setCompleted, activeStep, setActiveStep, referenceProjects, setApp, modified, setModified } = useAppContext();
  const { setFormData, getFormDataItem, updateDataValue, setResults } = useFormContext();
  const formData = useFormData();
  const { getProjectAPI, getCleaningPackagesAPI } = Server();
  const [sectionName, setSectionName] = useState('location');
  const [pages, setPages] = useState<PageSchema[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const lastCompletedStateRef = useRef<string>('');
  const mapRef = useRef<any>(null);
  const params = useParams();
  const navigate = useNavigate();

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const { data: cleaningPackages = {}, isSuccess: isCleaningPackagesLoaded } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
    enabled: role === 'mitigation_strategy' || role === 'assessment' || role === 'operational_strategy',
  });

  const {
    data: project = undefined,
    isError: isProjectError,
    error: projectError,
  } = useQuery({
    queryKey: ['project', params?.id],
    queryFn: () => getProjectAPI(params?.id as string),
    enabled: !!params?.id,
  });

  // A project that does not belong to the current user (or an orphan legacy project) now returns 404.
  // Surface a message and redirect to the projects list instead of rendering an empty/misleading form.
  useEffect(() => {
    if (isProjectError) {
      handleError(projectError);
      navigate('/projects', { replace: true });
    }
  }, [isProjectError, projectError, navigate]);

  const { checkCompleted, isValidForm, validateFields } = useProjectValidation(pages);
  const { isLoadingSensorData } = useSensorData({ sectionName, projectId: params?.id });
  const { isSaving, preservedPatternData, handleSave, handleDuplicate } = useProjectMutations({
    projectId: params?.id,
    project,
    isValidForm,
  });

  const handleComplete = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
  };

  useEffect(() => {
    setApp(Apps.SoilingMitigation);
    setActiveStep(0);
  }, []);

  useEffect(() => {
    setSectionName('location');
  }, [role]);

  // Leaving a section: re-enable any "external source" toggle (*.from_api) that was turned off but
  // never received a file, so the project never sits in an invalid (disabled + no pattern) state.
  const prevActiveStepRef = useRef(activeStep);
  useEffect(() => {
    if (prevActiveStepRef.current === activeStep) {
      return;
    }
    prevActiveStepRef.current = activeStep;

    let changed = false;
    const next: any = { ...formData };
    Object.keys(next).forEach((key) => {
      const block = next[key];
      if (block && typeof block === 'object' && block.from_api === false) {
        const pattern = block.rain_pattern ?? block.pattern;
        const hasValidPattern = pattern && typeof pattern !== 'string';
        if (!hasValidPattern) {
          next[key] = { ...block, from_api: true };
          changed = true;
        }
      }
    });
    if (changed) {
      setFormData(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    setPages((generatePages(referenceProjects, cleaningPackages, params?.id) as PageSchema[]).filter((step) => step.roles.includes(role)));
  }, [role, isCleaningPackagesLoaded, params?.id]);

  useEffect(() => {
    if (validateFields(pages[activeStep])) {
      const completedStateKey = `${activeStep}-${completed[activeStep]}`;
      if (lastCompletedStateRef.current !== completedStateKey) {
        handleComplete();
        lastCompletedStateRef.current = completedStateKey;
      }

      if (
        mapRef.current &&
        formData?.location?.latitude &&
        formData?.location?.longitude &&
        !isNaN(Number(formData.location.latitude)) &&
        !isNaN(Number(formData.location.longitude))
      ) {
        mapRef.current.setView([Number(formData.location.latitude), Number(formData.location.longitude)]);
      }
    }
  }, [formData, activeStep, pages]);

  useEffect(() => {
    const defaultFormData = {};
    if (pages && pages.length > 0) {
      pages.forEach((page) => {
        const defaultValues = page.default_values;
        if (defaultValues) {
          Object.keys(defaultValues).forEach((key) => {
            const existingValue = getFormDataItem(formData, key);
            if (existingValue === undefined || existingValue === null || existingValue === '') {
              updateDataValue(defaultFormData, key, defaultValues[key]);
            }
          });
        }
      });
    }

    if (!project && isCleaningPackagesLoaded && cleaningPackages && Object.keys(cleaningPackages).length > 0) {
      if (!formData.cleaning_packages) {
        updateDataValue(defaultFormData, 'cleaning_packages', cleaningPackages);
      }
    }

    if (project || Object.keys(defaultFormData).length > 0) {
      const mergedData = {
        ...formData,
        ...defaultFormData,
        ...project,
      };
      const transformedData = transformSoilingSensorDataFromArray(mergedData);

      const finalData = { ...transformedData };

      const transformedSoilingKeys = getSoilingBlockKeys(transformedData);

      transformedSoilingKeys.forEach((transformedKey) => {
        let patternToRestore = null;

        if (preservedPatternData[transformedKey]) {
          patternToRestore = preservedPatternData[transformedKey];
        } else {
          const transformedFileUid = getFormDataItem(transformedData, `${transformedKey}.file_uid`);
          if (transformedFileUid) {
            Object.keys(preservedPatternData).forEach((preservedKey) => {
              const preservedFileUid = getFormDataItem(formData, `${preservedKey}.file_uid`);
              if (preservedFileUid === transformedFileUid) {
                patternToRestore = preservedPatternData[preservedKey];
              }
            });
          }
        }

        if (patternToRestore) {
          updateDataValue(finalData, `${transformedKey}.pattern`, patternToRestore);
        }
      });

      setFormData(finalData);
    } else if (!formData || Object.keys(formData).length === 0) {
      setFormData({});
    }

    if (modified) {
      setModified(false);
    }
    setResults(project?.results);

    document.title = 'Project | ' + (project?.name ? project?.name : 'Assessment');
  }, [project, pages, cleaningPackages, isCleaningPackagesLoaded]);

  const transformSoilingSensorDataFromArray = (data: any) => {
    if (!data || !data.soiling_sensor_data || !Array.isArray(data.soiling_sensor_data)) {
      return data;
    }

    const transformedData = { ...data };
    const soilingSensorArray = transformedData.soiling_sensor_data;

    delete transformedData.soiling_sensor_data;

    getSoilingBlockKeys(transformedData).forEach((key) => {
      delete (transformedData as any)[key];
    });

    soilingSensorArray.forEach((item: any, index: number) => {
      const blockKey = index === 0 ? 'soiling_sensor_data' : `soiling_sensor_data_${index + 1}`;
      (transformedData as any)[blockKey] = {
        file_uid: item.file_uid,
        _order: item._order || index + 1,
        name: item.name || '',
        type: item.type || 'rde300',
      };
    });

    return transformedData;
  };

  React.useEffect(() => {
    if (modified && params?.id) {
      handleSave();
    }
  }, [modified]);

  const drawerWidth = 240;

  const hasLocation = !!(formData?.location?.latitude && formData?.location?.longitude);

  const sectionResults: { section: string; ready: boolean; content: React.ReactNode; fallback: string }[] = [
    {
      section: 'soiling_risk_assessment',
      ready: !!(hasLocation && formData.surface_tilt != undefined && formData.cleaning_efficiency),
      content: <AppSoilingAssessment name={''} projectId={project?.id} setResults={setResults} />,
      fallback: 'Missing required fields.',
    },
    {
      section: 'mitigation_strategy',
      ready: hasLocation,
      content: <AppSoilingMitigationStrategy projectId={project?.id} setResults={setResults} />,
      fallback: 'Please choose a location to see results.',
    },
    {
      section: 'operational_strategy',
      ready: hasLocation,
      content: <AppSoilingOperationalStrategy setResults={setResults} project_id={params?.id as string} />,
      fallback: 'Please choose a location to see results.',
    },
    {
      section: 'ex_post_analysis',
      ready: hasLocation,
      content: <AppSoilingOperationalStrategy type={'ex-post'} setResults={setResults} project_id={params?.id as string} />,
      fallback: 'Please choose a location to see results.',
    },
  ];

  return (
    <Box sx={{ margin: 0, height: '100vh' }}>
      <Toolbar />
      <Box sx={{ display: 'flex' }}>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Toolbar />
          <Toolbar>
            <Grid container>
              <Grid sx={{ width: '100%', mt: 2 }}>
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  aria-owns={open ? 'mouse-over-popover' : undefined}
                  aria-haspopup="true"
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                >
                  {formData?.name || 'Plant Name'}
                </Typography>
                {formData?.name && (
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: 'none' }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>{formData?.name}</Typography>
                  </Popover>
                )}
              </Grid>
              <Grid>
                {!project && (
                  <IconButton aria-label="save" onClick={handleSave} loading={isSaving}>
                    <SaveIcon sx={{ color: modified ? 'red' : 'none' }} />
                  </IconButton>
                )}
                {project && (
                  <IconButton aria-label="duplicate" onClick={handleDuplicate} loading={isSaving}>
                    <ContentCopyIcon sx={{ color: 'none' }} />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Toolbar>
          <Divider />
          <List>
            {pages?.map((page, index) => {
              if (page.roles.includes(role)) {
                return page.divider ? (
                  <Divider />
                ) : (
                  <ListItem key={page.name} disablePadding>
                    <ListItemButton
                      sx={{
                        borderLeft: sectionName === page.section ? '5px solid #007acd' : '5px solid rgba(0, 0, 0, 0)',
                      }}
                      selected={sectionName === page.section}
                      onClick={() => {
                        if (isValidForm()) {
                          setActiveStep(index);
                          setSectionName(page.section);
                          page.onClick?.();
                        }
                      }}
                      disabled={(page.hasOwnProperty('disabled') && page.disabled) || checkCompleted(page.prerequisites)}
                    >
                      <ListItemIcon>{page.icon}</ListItemIcon>
                      <ListItemText primary={page.name} />
                    </ListItemButton>
                  </ListItem>
                );
              }
            })}
            <>
              <Divider />
              {ROLE_SECTIONS.filter((item) => item.role === role).map((item) => (
                <ListItem key={item.section} disablePadding>
                  <ListItemButton
                    sx={{ borderLeft: sectionName === item.section ? '5px solid #007acd' : '' }}
                    selected={sectionName === item.section}
                    disabled={!project}
                    onClick={() => {
                      setActiveStep(-1);
                      setSectionName(item.section);
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            width: { xs: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          {!project && (modified || isSaving) ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have unsaved changes. Click{' '}
              <Link onClick={handleSave} sx={{ cursor: 'pointer' }}>
                here
              </Link>{' '}
              to save your project.
            </Alert>
          ) : null}
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', p: 1 }} noValidate>
            <Grid container spacing={2}>
              {pages[activeStep]?.description ? (
                <Box sx={{ mt: 1, mb: 2 }}>
                  {typeof pages[activeStep]?.description === 'object' && pages[activeStep]?.description !== null
                    ? pages[activeStep]?.description[role] || pages[activeStep]?.description
                    : pages[activeStep]?.description}
                </Box>
              ) : null}
              <FormRenderer fields={pages[activeStep]?.fields ?? []} mapRef={mapRef} isLoadingSensorData={isLoadingSensorData} />
            </Grid>
          </Box>
          {sectionResults
            .filter((s) => s.section === sectionName)
            .map((s) => (
              <Box key={s.section} sx={{ mt: 0 }}>
                {s.ready ? s.content : <Typography color="text.primary">{s.fallback}</Typography>}
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Project;
